import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { InvestmentMetrics } from '../../service/lender-details.service';
import { LoanDetailsService } from '../../service/loan-details.service';
import { Keys, StorageService } from '../../service/storage.service';
import { WriteOffService } from '../../service/write-off.service';
import { Loan } from '../../model/loan.model';
import { EmiStatus } from '../../model/emi-status';
import * as moment from 'moment';
import { LoanStatusType } from '../../model/loan-status.model';
import { WriteOff, WriteOffHistory, WriteOffTag } from '../../model/WriteOff';
import { animate, state, style, transition, trigger } from '@angular/animations';

interface LenderWrittenOffLoan {
    writtenOffLoanId: string;
    writeOffStatus: string;
    investmentDate: string;
    investmentAmount: number;
    receivedEMICount: number;
    delayedEMICount: number;
    writeOffDate: string;
    principalWrittenOff: number;
    loanIdentifier: string;
}

@Component({
    selector: 'app-lender-write-off-loans',
    templateUrl: './lender-write-off-loans.component.html',
    styleUrls: ['./lender-write-off-loans.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*', minHeight: "*" })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],

})
export class LenderWriteOffLoansComponent implements OnInit {

    public displayedColumns = ['writtenOffLoanId', 'writeOffStatus',
        'investmentDate',
        'investmentAmount',
        'receivedEMICount',
        'delayedEMICount',
        'writeOffDate',
        'principalWrittenOff'
    ];
    writeOffDataSource = new MatTableDataSource();
    name: string;
    metrics: InvestmentMetrics;
    networkCallInProgress: boolean;
    private loanDetailsService: LoanDetailsService;
    private storageService: StorageService;
    private writeOffService: WriteOffService;
    expandedElement: any | null;
    public isMobile: boolean = false;
    screenWidth: number;

    constructor(loanDetailsService: LoanDetailsService, storageService: StorageService,
        writeOffService: WriteOffService) {
        this.storageService = storageService;
        this.loanDetailsService = loanDetailsService;
        this.writeOffService = writeOffService;
    }
    ngAfterViewInit() {
        this.screenWidth = window.innerWidth;
        this.isMobile = (<any>window).innerWidth < 500;
        if (this.isMobile) {
            this.displayedColumns = ['writtenOffLoanId', 'writeOffStatus',
                'investmentDate','accordion_arrow'];
        }
    }

    async ngOnInit(): Promise<void> {
        this.networkCallInProgress = true;
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const writtenOffLoans = await this.loanDetailsService.getWrittenOffLoansFor(lenderId);
        this.writeOffDataSource.data = await this.populateWrittenOffDataForLoans(writtenOffLoans, lenderId);
        this.networkCallInProgress = false;
    }

    private async populateWrittenOffDataForLoans(writtenOffLoans: Loan[], lenderId: string): Promise<LenderWrittenOffLoan[]> {
        const lenderWrittenOffLoan: LenderWrittenOffLoan[] = [];
        for (let i = 0; i < writtenOffLoans.length; i++) {
            const loan = writtenOffLoans[i];
            const writeOffDetailsForLoan = await this.writeOffService.getWriteOffByLoanId(loan._id);
            const investmentDetails = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
            const lenderShare = investmentDetails.invested_amount / loan.loan_fulfilled_amount;
            const receivedEmis = loan.emi_schedule.filter(emi => emi.status === EmiStatus.Completed || emi.status === EmiStatus.Received);
            const delayedEmis = loan.emi_schedule.filter(emi => emi.status !== EmiStatus.Completed
                && emi.status !== EmiStatus.Received && moment(emi.due_on).valueOf() <= moment().valueOf());

            lenderWrittenOffLoan.push({
                writtenOffLoanId: loan.generated_loan_id,
                writeOffStatus: this.getDisplayNameForWriteOffStatus(loan.loan_sanction_status),
                investmentDate: moment(investmentDetails.timestamp).format('DD MMM YYYY'),
                investmentAmount: investmentDetails.invested_amount,
                receivedEMICount: receivedEmis ? receivedEmis.length : 0,
                delayedEMICount: delayedEmis ? delayedEmis.length : 0,
                writeOffDate: moment(this.getWriteOffDate(loan.loan_sanction_status, writeOffDetailsForLoan)).format('DD MMM YYYY'),
                principalWrittenOff: writeOffDetailsForLoan.principal * lenderShare,
                loanIdentifier: loan._id
            });
        }

        return lenderWrittenOffLoan;
    }

    private getWriteOffBy(writeOffTag: WriteOffTag, writtenOff: WriteOff): WriteOffHistory {
        return writtenOff.history.find(history => history.tag === writeOffTag);
    }


    private getDisplayNameForWriteOffStatus(status: LoanStatusType): string {
        switch (status) {
            case LoanStatusType.SubStandardAsset:
                return 'Sub Standard Asset';
            case LoanStatusType.LossAsset:
                return 'Loss Asset';
        }
    }

    private getWriteOffDate(loan_sanction_status: LoanStatusType, writeOffDetailsForLoan: WriteOff): Date {
        if (loan_sanction_status === LoanStatusType.SubStandardAsset) {
            return this.getWriteOffBy(WriteOffTag.SubStandardAsset, writeOffDetailsForLoan).createdAt;
        }
        return this.getWriteOffBy(WriteOffTag.LossAsset, writeOffDetailsForLoan).createdAt;
    }
}
